<template>
  <Sheet v-model="isOpen" size="md" content-class="p-0">
    <template #header>
      <div v-if="title" class="flex items-center gap-3 min-w-0">
        <!-- Small poster -->
        <div class="w-10 overflow-hidden rounded flex-shrink-0">
          <img
            v-if="title.poster_path"
            :src="`https://image.tmdb.org/t/p/w92${title.poster_path}`"
            :alt="displayTitle"
            class="object-cover w-full" />
          <div
            v-else
            class="flex items-center justify-center w-full h-14 bg-gray-700">
            <fa-icon :icon="['far', 'film']" class="text-gray-500 text-xs" />
          </div>
        </div>
        <!-- Title info -->
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-medium text-white truncate">
            {{ displayTitle }}
          </h3>
          <div class="flex items-center gap-2 text-xs text-gray-400">
            <span v-if="releaseYear">{{ releaseYear }}</span>
            <span v-if="titleType" class="uppercase">{{ titleType }}</span>
          </div>
        </div>
      </div>
      <span v-else class="text-lg font-semibold text-white">Note</span>
    </template>

    <div class="flex flex-col">
      <!-- Error state -->
      <div
        v-if="error"
        class="flex items-center p-3 mx-4 mt-4 text-xs text-red-500 rounded-md bg-red-500/10 border border-red-500/20">
        <fa-icon icon="triangle-exclamation" class="mr-2" />
        <p>{{ error }}</p>
      </div>

      <!-- Loading state -->
      <div
        v-if="loading && !currentNote && !isCreatingNewNote"
        class="flex items-center justify-center p-8">
        <div class="animate-pulse text-gray-400">Loading note...</div>
      </div>

      <!-- Note form content -->
      <div v-else class="px-4 pt-4">
        <form id="note-form" class="space-y-4" @submit.prevent="submitNote">
          <div>
            <label
              class="block mb-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
              {{ noteLabel }}
            </label>
            <textarea
              v-model="noteContent"
              class="w-full p-3 text-sm text-white rounded-lg border border-gray-700 bg-gray-800 resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              :class="{
                'cursor-not-allowed bg-gray-900 opacity-60': !canEdit,
                'cursor-text': canEdit
              }"
              rows="5"
              :readonly="!canEdit"
              :disabled="!canEdit"
              placeholder="Add your thoughts about this title..."></textarea>
          </div>
        </form>
      </div>

      <!-- Action buttons footer (1/3 - 2/3 split) -->
      <div v-if="canEdit" class="p-4 border-t border-white/10 mt-4">
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-600 active:bg-gray-700 active:scale-[0.98] transition-all"
            @click="close">
            Cancel
          </button>
          <button
            type="submit"
            form="note-form"
            class="flex-[2] py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 active:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600 disabled:active:scale-100"
            :disabled="loading || !noteContent.trim()">
            {{
              loading
                ? 'Saving...'
                : isCreatingNewNote
                  ? 'Add Note'
                  : 'Save Note'
            }}
          </button>
        </div>
      </div>

      <!-- Read-only footer -->
      <div v-else class="p-4 border-t border-white/10 mt-4">
        <button
          type="button"
          class="w-full py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-600 active:bg-gray-700 active:scale-[0.98] transition-all"
          @click="close">
          Close
        </button>
      </div>
    </div>
  </Sheet>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStoreNotes } from '@/stores/storeNotes'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreLists } from '@/stores/storeLists'
import { Sheet } from '@/components/Options'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['list', 'list_item', 'review', 'title'].includes(value)
  },
  listItem: Object,
  list: Object,
  review: Object,
  title: Object,
  titleType: String
})

const emit = defineEmits(['update:modelValue', 'close'])

const storeProfile = useStoreProfile()
const storeNotes = useStoreNotes()
const storeLists = useStoreLists()

const loading = ref(false)
const error = ref('')
const isCreatingNewNote = ref(false)
const currentNote = ref(null)
const noteContent = ref('')

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
    if (!value) emit('close')
  }
})

// Title display helpers
const displayTitle = computed(() => {
  return props.title?.title || props.title?.name || 'Unknown Title'
})

const releaseYear = computed(() => {
  const date = props.title?.release_date || props.title?.first_air_date
  if (date) {
    return new Date(date).getFullYear()
  }
  return null
})

// Get the object ID based on type
const objectId = computed(() => {
  switch (props.type) {
    case 'list':
      return props.list?.id
    case 'list_item':
      return props.listItem?.id
    case 'review':
      return props.review?.id
    case 'title':
      return props.title?.id
    default:
      return null
  }
})

// Check if current user can edit
const canEdit = computed(() => {
  if (isCreatingNewNote.value) {
    // Only allow creating notes on items the user owns
    if (props.type === 'list_item'
      && props.listItem?.user_id !== storeProfile.currentUserId) {
      return false
    }
    return !!storeProfile.currentUserId
  }
  // For existing notes, only the note owner can edit
  return currentNote.value?.user_id === storeProfile.currentUserId
})

// Label for the note field. No other author to name: a loaded note is this
// user's own, and the read-only case is a list item of somebody else's with
// nothing on it.
const noteLabel = computed(() => {
  return isCreatingNewNote.value || canEdit.value ? 'Your Note' : 'Note'
})

// Fetch note when sheet opens
watch(
  isOpen,
  async (open) => {
    if (open) {
      await fetchNote()
    } else {
      // Reset state when closed
      noteContent.value = ''
      currentNote.value = null
      error.value = ''
    }
  },
  { immediate: true }
)

async function fetchNote() {
  if (!objectId.value || !props.type) return

  loading.value = true
  error.value = ''
  isCreatingNewNote.value = false

  try {
    // For list items, use the notes already embedded on the item prop rather
    // than fetching them again. Both come back the same: a note is readable by
    // its author alone, so either route returns this user's note or nothing.
    const embeddedNotes = props.type === 'list_item'
      ? props.listItem?.notes
      : null

    let notes
    if (embeddedNotes?.length) {
      notes = embeddedNotes
    } else {
      // Fallback to fetching from API for other note types
      notes = await storeNotes.fetchNotesByObject(
        props.type,
        objectId.value
      )
    }

    const userNote = notes?.find(
      (note) => note.user_id === storeProfile.currentUserId
    )

    if (userNote) {
      currentNote.value = userNote
      noteContent.value = userNote.content || ''
    } else {
      // No note - show empty form for creating only if user owns the item
      currentNote.value = null
      noteContent.value = ''
      if (props.type === 'list_item'
        && props.listItem?.user_id !== storeProfile.currentUserId) {
        // Someone else's list item: nothing to read, nothing to add
        isCreatingNewNote.value = false
      } else {
        isCreatingNewNote.value = true
      }
    }
  } catch (err) {
    error.value = 'Failed to load note: ' + (err.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

async function submitNote() {
  if (!noteContent.value.trim()) return

  loading.value = true
  error.value = ''

  try {
    if (isCreatingNewNote.value) {
      // Create new note. Every note is private to its author; the server
      // writes is_private true and ignores what a client sends.
      await storeNotes.createNote({
        object_type: props.type,
        [`${props.type}_id`]: objectId.value,
        content: noteContent.value.trim()
      })
    } else {
      // Update existing note
      await storeNotes.updateNote(currentNote.value.id, {
        content: noteContent.value.trim()
      })
    }

    // Refresh the list item to update notes
    if (props.type === 'list_item' && props.listItem?.id) {
      storeLists.fetchListItemById(props.listItem.id)
    }

    close()
  } catch (err) {
    error.value = 'Failed to save note: ' + (err.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

function close() {
  isOpen.value = false
}
</script>
